// src/pages/guides/ExposedSSHGuide.js
// Exposed SSH Response Guide - SEO Playbook Page

import React from 'react';
import useTrialCta from '../../hooks/useTrialCta';
import GuideByline from './GuideByline';
import GuideAnswer from './GuideAnswer';
import GuideFAQ, { buildFAQPageSchema } from './GuideFAQ';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/common/SEOHead';
import { PublicHeader, PublicFooter } from '../../components/common';
import './Guides.css';

function ExposedSSHGuide() {
  const startTrial = useTrialCta('guide_trial:exposed-ssh');

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Exposed SSH: Detection, Hardening, and Continuous Monitoring",
    "description": "Detect exposed SSH, harden port 22, and monitor for configuration drift. You hardened SSH once — continuous monitoring ensures it stays hardened.",
    "image": ["https://www.driftalarm.com/images/og-image.png"],
    "datePublished": "2025-12-01T00:00:00-05:00",
    "dateModified": "2026-08-25T00:00:00-04:00",
    "author": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com" },
    "publisher": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com", "logo": { "@type": "ImageObject", "url": "https://www.driftalarm.com/images/og-image.png" } },
    "url": "https://www.driftalarm.com/guides/exposed-ssh",
    "mainEntityOfPage": "https://www.driftalarm.com/guides/exposed-ssh",
    "keywords": ["exposed SSH monitoring", "SSH continuous security", "SSH hardening guide", "exposed SSH detection", "SSH port 22 security"]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.driftalarm.com" },
      { "@type": "ListItem", "position": 2, "name": "Security Guides", "item": "https://www.driftalarm.com/guides" },
      { "@type": "ListItem", "position": 3, "name": "Exposed SSH" }
    ]
  };

  return (
    <div className="guide-container">
      <SEOHead
        title="Exposed SSH: Detection & Continuous Monitoring | DriftAlarm"
        description="Detect exposed SSH, harden port 22, and monitor for configuration drift. You hardened SSH once — continuous monitoring ensures it stays hardened."
        path="/guides/exposed-ssh"
        ogType="article"
        jsonLd={[articleSchema, breadcrumbSchema, buildFAQPageSchema('/guides/exposed-ssh')].filter(Boolean)}
      />
      <PublicHeader activeLink="guides" />

      <main className="guide-content">
        <section className="guide-hero">
          <h1>Exposed SSH: Detection, Hardening, and Continuous Monitoring</h1>
          <GuideByline path="/guides/exposed-ssh" />
          <GuideAnswer path="/guides/exposed-ssh" />
          <p className="guide-hero-intro">
            You hardened SSH once — but is it still hardened? SSH exposed to the internet receives constant
            automated attacks, and configurations drift over time. Password authentication gets re-enabled
            during troubleshooting, new servers deploy with default settings, and firewall rules change.
            Continuous monitoring detects when your SSH security posture degrades.
          </p>

          <div className="guide-hero-bullets">
            <div className="guide-hero-bullets-title">What You'll Learn</div>
            <ul>
              <li>Why exposed SSH attracts immediate automated attacks</li>
              <li>The difference between secure and insecure SSH configurations</li>
              <li>How to harden SSH for internet exposure</li>
              <li>When SSH exposure is acceptable vs. when it's P1</li>
              <li>Key-based authentication and MFA requirements</li>
            </ul>
          </div>

          <div className="guide-cta-group">
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>→</span>
            </button>
            <Link to="/guides/attack-surface-monitoring" className="da-btn da-btn--secondary">
              Back to Attack Surface Monitoring Guide
            </Link>
          </div>
        </section>

        <nav className="guide-toc">
          <div className="guide-toc-title">In This Guide</div>
          <ul className="guide-toc-list">
            <li><a href="#risk-assessment">Risk Assessment</a></li>
            <li><a href="#attack-landscape">The Attack Landscape</a></li>
            <li><a href="#secure-vs-insecure">Secure vs. Insecure SSH</a></li>
            <li><a href="#immediate-hardening">Immediate Hardening</a></li>
            <li><a href="#long-term">Long-Term Security</a></li>
            <li><a href="#monitoring">Monitoring & Detection</a></li>
            <li><a href="#continuous-ssh-monitoring">Continuous SSH Monitoring</a></li>
            <li><a href="#faq">Frequently Asked Questions</a></li>
          </ul>
        </nav>

        <section id="risk-assessment" className="guide-section">
          <h2>Risk Assessment: When Is SSH Exposure a P1?</h2>

          <p>
            Unlike RDP, SSH exposure isn't automatically a P1. The risk depends heavily on configuration:
          </p>

          <div className="callout callout-critical">
            <div className="callout-title">Instant P1: Password Authentication Enabled</div>
            <div className="callout-content">
              <p>
                <strong>SSH with password authentication enabled and exposed to the internet is a P1.</strong>
                Password-based SSH is routinely compromised through brute force and credential stuffing.
                Attackers have massive password lists and botnets dedicated to SSH brute forcing.
              </p>
            </div>
          </div>

          <div className="callout callout-warning">
            <div className="callout-title">P2-P3: Key-Only with Hardening Gaps</div>
            <div className="callout-content">
              <p>
                SSH with key-only authentication but missing other hardening measures (root login allowed,
                outdated version, weak key algorithms) is P2-P3 depending on specifics.
              </p>
            </div>
          </div>

          <div className="callout callout-info">
            <div className="callout-title">P4: Properly Hardened SSH</div>
            <div className="callout-content">
              <p>
                SSH with key-only auth, disabled root login, current version, and strong algorithms is
                the lowest risk. Still monitor for attacks and consider VPN/bastion architecture for
                sensitive systems.
              </p>
            </div>
          </div>

          <h3>Quick Assessment Checklist</h3>
          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Configuration</th>
                  <th>Risk Level</th>
                  <th>Priority</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Password authentication enabled</td>
                  <td>Critical</td>
                  <td><span className="priority-badge p1">P1</span></td>
                </tr>
                <tr>
                  <td>Root login permitted</td>
                  <td>High</td>
                  <td><span className="priority-badge p1">P1</span></td>
                </tr>
                <tr>
                  <td>Outdated OpenSSH version (known CVEs)</td>
                  <td>High</td>
                  <td><span className="priority-badge p1">P1</span></td>
                </tr>
                <tr>
                  <td>Weak key exchange algorithms</td>
                  <td>Medium</td>
                  <td><span className="priority-badge p2">P2</span></td>
                </tr>
                <tr>
                  <td>No fail2ban or rate limiting</td>
                  <td>Medium</td>
                  <td><span className="priority-badge p3">P3</span></td>
                </tr>
                <tr>
                  <td>Key-only, hardened, current version</td>
                  <td>Low</td>
                  <td><span className="priority-badge p4">P4</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section id="attack-landscape" className="guide-section">
          <h2>The Attack Landscape</h2>

          <div className="callout callout-warning">
            <div className="callout-title">Constant Automated Scanning</div>
            <div className="callout-content">
              <p>
                <strong>Every SSH server on the internet receives thousands of login attempts per day.</strong>
                This isn't targeted—it's automated botnets scanning the entire IPv4 space continuously.
                Within minutes of exposing port 22, your server will be discovered and attacked.
              </p>
            </div>
          </div>

          <h3>Attack Volume Reality</h3>
          <ul>
            <li>Average internet-facing SSH server: thousands to tens of thousands of failed login attempts daily</li>
            <li>Peak attack volume: significantly higher during active botnet campaigns</li>
            <li>Time to first attack after exposure: often within minutes of going online</li>
            <li>Common usernames targeted: root, admin, ubuntu, test, guest, user, oracle, postgres</li>
          </ul>

          <h3>Attack Types</h3>
          <ul>
            <li><strong>Brute force:</strong> Trying common passwords (123456, password, admin123)</li>
            <li><strong>Credential stuffing:</strong> Using leaked username/password pairs from breaches</li>
            <li><strong>Dictionary attacks:</strong> Systematic password guessing with wordlists</li>
            <li><strong>Exploit attempts:</strong> Targeting known SSH vulnerabilities</li>
            <li><strong>Post-compromise:</strong> Crypto mining, botnet recruitment, lateral movement</li>
          </ul>

          <div className="example-box">
            <div className="example-title">What Attackers Do After SSH Compromise</div>
            <div className="example-content">
              <p>
                Once attackers gain SSH access, typical actions include:
              </p>
              <ul>
                <li>Installing cryptocurrency miners (immediate monetization)</li>
                <li>Adding the server to botnets for DDoS or further scanning</li>
                <li>Pivoting to internal network resources</li>
                <li>Exfiltrating data or credentials</li>
                <li>Establishing persistence for later ransomware deployment</li>
              </ul>
            </div>
          </div>
        </section>

        <section id="secure-vs-insecure" className="guide-section">
          <h2>Secure vs. Insecure SSH Configuration</h2>

          <h3>Insecure Configuration (Common Defaults)</h3>
          <div className="scoring-formula">
            <div className="scoring-formula-title">sshd_config - INSECURE</div>
            <div className="scoring-formula-line"><code>PasswordAuthentication yes</code> ← Allows brute force</div>
            <div className="scoring-formula-line"><code>PermitRootLogin yes</code> ← Direct root access</div>
            <div className="scoring-formula-line"><code>PermitEmptyPasswords yes</code> ← Critical vulnerability</div>
            <div className="scoring-formula-line"><code># No MaxAuthTries limit</code> ← Unlimited attempts</div>
          </div>

          <h3>Secure Configuration</h3>
          <div className="scoring-formula">
            <div className="scoring-formula-title">sshd_config - HARDENED</div>
            <div className="scoring-formula-line"><code>PasswordAuthentication no</code> ← Key-only</div>
            <div className="scoring-formula-line"><code>PermitRootLogin no</code> ← No direct root</div>
            <div className="scoring-formula-line"><code>PubkeyAuthentication yes</code> ← Require keys</div>
            <div className="scoring-formula-line"><code>MaxAuthTries 3</code> ← Limit attempts</div>
            <div className="scoring-formula-line"><code>AllowUsers specificuser</code> ← Explicit allowlist</div>
            <div className="scoring-formula-line"><code>KexAlgorithms / Ciphers</code> ← Pin to modern algorithms</div>
          </div>

          <div className="callout callout-info">
            <div className="callout-title">Key-Based Authentication Is Essential</div>
            <div className="callout-content">
              <p>
                SSH key authentication is exponentially more secure than passwords:
              </p>
              <ul>
                <li>Ed25519 or RSA 3072-4096 keys—not brute-forceable in practice</li>
                <li>No password to phish, stuff, or guess</li>
                <li>Private key never leaves your machine</li>
                <li>Can require passphrase on key for additional security</li>
              </ul>
            </div>
          </div>
        </section>

        <section id="immediate-hardening" className="guide-section">
          <h2>Immediate Hardening Steps</h2>

          <p>
            If you've discovered exposed SSH with password authentication, take these steps immediately:
          </p>

          <h3>Step 1: Assess Current Risk (15 minutes)</h3>
          <ul>
            <li>Check if password authentication is enabled: <code>grep PasswordAuthentication /etc/ssh/sshd_config</code></li>
            <li>Check if root login is permitted: <code>grep PermitRootLogin /etc/ssh/sshd_config</code></li>
            <li>Review recent authentication attempts: <code>grep sshd /var/log/auth.log | tail -100</code></li>
            <li>Check for successful logins from unknown IPs</li>
          </ul>

          <h3>Step 2: Disable Password Authentication (30 minutes)</h3>
          <div className="callout callout-warning">
            <div className="callout-title">Before Disabling Passwords</div>
            <div className="callout-content">
              <p>
                <strong>Ensure you have SSH key access configured before disabling password authentication.</strong>
                Test key-based login in a separate session before making changes. Locking yourself out
                of a remote server is a real risk.
              </p>
            </div>
          </div>

          <ol>
            <li>Generate SSH key pair on your local machine (if you don't have one)</li>
            <li>Copy public key to server's authorized_keys</li>
            <li>Test key-based login in new terminal (keep existing session open)</li>
            <li>Edit /etc/ssh/sshd_config: set <code>PasswordAuthentication no</code></li>
            <li>Set <code>PermitRootLogin no</code></li>
            <li>Restart SSH service: <code>sudo systemctl restart sshd</code></li>
            <li>Verify password login is rejected</li>
          </ol>

          <h3>Step 3: Implement Rate Limiting (30 minutes)</h3>
          <ul>
            <li>Install fail2ban: <code>sudo apt install fail2ban</code></li>
            <li>Configure SSH jail with aggressive ban settings</li>
            <li>Consider iptables rate limiting as additional layer</li>
          </ul>

          <h3>Step 4: Check for Compromise (1 hour)</h3>
          <ul>
            <li>Review all successful SSH logins during exposure period</li>
            <li>Check for new user accounts or authorized_keys modifications</li>
            <li>Look for unexpected processes, especially crypto miners</li>
            <li>Check cron jobs and systemd services for persistence</li>
            <li>Review network connections for suspicious outbound traffic</li>
          </ul>
        </section>

        <section id="long-term" className="guide-section">
          <h2>Long-Term Security Architecture</h2>

          <h3>Option 1: Bastion Host / Jump Box</h3>
          <p>
            Route all SSH access through a hardened bastion host:
          </p>
          <ul>
            <li>Single, hardened entry point with maximum logging</li>
            <li>Internal servers not directly exposed to internet</li>
            <li>MFA on bastion authentication</li>
            <li>Session recording for audit</li>
          </ul>

          <h3>Option 2: VPN-Only Access</h3>
          <p>
            Require VPN before SSH is accessible:
          </p>
          <ul>
            <li>SSH port only accessible from VPN IP ranges</li>
            <li>VPN requires MFA</li>
            <li>Defense in depth—two authentication layers</li>
          </ul>

          <h3>Option 3: Zero Trust SSH</h3>
          <p>
            Modern identity-aware access:
          </p>
          <ul>
            <li>Certificate-based SSH authentication</li>
            <li>Short-lived certificates issued after MFA</li>
            <li>No static keys to manage or compromise</li>
            <li>Examples: Teleport, Boundary, Cloudflare Access</li>
          </ul>

          <div className="callout callout-warning">
            <div className="callout-title">MFA for Administrative Access</div>
            <div className="callout-content">
              <p>
                <strong>Any administrative access to production systems should require MFA.</strong>
                This includes SSH. Options include:
              </p>
              <ul>
                <li>MFA at VPN/bastion level (most common)</li>
                <li>PAM-based MFA (Google Authenticator PAM module)</li>
                <li>Certificate-based auth with MFA-gated certificate issuance</li>
              </ul>
            </div>
          </div>
        </section>

        <section id="monitoring" className="guide-section">
          <h2>Monitoring & Detection</h2>

          <h3>What to Monitor</h3>
          <ul>
            <li><strong>Failed authentication attempts:</strong> Baseline and alert on spikes</li>
            <li><strong>Successful logins from new IPs:</strong> Especially for privileged accounts</li>
            <li><strong>Logins outside business hours:</strong> Unusual access patterns</li>
            <li><strong>Geographic anomalies:</strong> Logins from unexpected countries</li>
            <li><strong>Configuration changes:</strong> Modifications to sshd_config or authorized_keys</li>
          </ul>

          <h3>Log Sources</h3>
          <ul>
            <li><code>/var/log/auth.log</code> (Debian/Ubuntu)</li>
            <li><code>/var/log/secure</code> (RHEL/CentOS)</li>
            <li>Systemd journal: <code>journalctl -u sshd</code></li>
            <li>Centralized SIEM for correlation across hosts</li>
          </ul>

          <h3>Alert Thresholds</h3>
          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Threshold</th>
                  <th>Response</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Failed auth attempts (single IP)</td>
                  <td>&gt;10 in 5 minutes</td>
                  <td>Auto-block IP (fail2ban)</td>
                </tr>
                <tr>
                  <td>Failed auth attempts (aggregate)</td>
                  <td>&gt;100 in 1 hour</td>
                  <td>Alert security team</td>
                </tr>
                <tr>
                  <td>Successful root login</td>
                  <td>Any</td>
                  <td>Immediate alert</td>
                </tr>
                <tr>
                  <td>Login from new country</td>
                  <td>Any</td>
                  <td>Alert and verify</td>
                </tr>
                <tr>
                  <td>authorized_keys modified</td>
                  <td>Any</td>
                  <td>Immediate alert</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section id="continuous-ssh-monitoring" className="guide-section">
          <h2>Continuous SSH Monitoring: Detecting Configuration Drift</h2>
          <p>
            Hardening SSH is a point-in-time action. Without continuous monitoring, configurations drift:
            a colleague re-enables password auth to troubleshoot a login issue, a new server deploys with
            default settings, or a firewall rule change unexpectedly exposes port 22 on a production system.
          </p>
          <p>
            <Link to="/guides/configuration-drift">Configuration drift</Link> is how security breaks quietly.
            External monitoring detects these changes by scanning from the outside — the same perspective an attacker
            has — and alerting when your SSH security posture degrades.
          </p>
          <div className="callout callout-info">
            <div className="callout-title">What Continuous SSH Monitoring Detects</div>
            <div className="callout-content">
              <ul>
                <li><strong>New SSH exposure:</strong> Port 22 appearing on an asset where it wasn't before</li>
                <li><strong>Authentication method changes:</strong> Detecting when password auth is re-enabled</li>
                <li><strong>Version changes:</strong> Tracking OpenSSH version across scans to catch downgrades</li>
                <li><strong>Cipher suite drift:</strong> Identifying when <Link to="/guides/tls-ssl-drift">secure configurations weaken</Link></li>
                <li><strong>Banner changes:</strong> Detecting service modifications that may indicate compromise</li>
              </ul>
            </div>
          </div>
          <div className="guide-cta-section">
            <h3>How DriftAlarm Helps</h3>
            <p>DriftAlarm's drift detection engine monitors your SSH configurations continuously from the outside. When any SSH-related change occurs — new exposure, version change, or configuration drift — you get an alert with full context.</p>
            <div className="guide-cta-buttons">
              <button onClick={startTrial} className="da-btn da-btn--primary">Start Free 30-Day Trial</button>
              <Link to="/how-it-works" className="da-btn da-btn--secondary">See How It Works</Link>
            </div>
          </div>
        </section>

        <section className="guide-section">
          <h2>Related Security Guides</h2>
          <p>
            SSH exposure is often found alongside other attack surface issues. Review these related guides:
          </p>
          <ul>
            <li><Link to="/guides/configuration-drift">Configuration Drift Guide</Link> — Understand how
                security configurations change over time and why drift detection is essential for ongoing SSH security.</li>
            <li><Link to="/guides/tls-ssl-drift">TLS/SSL Drift Guide</Link> — Certificate and cipher suite
                changes often affect the same servers running SSH — monitor both protocols.</li>
            <li><Link to="/guides/exposed-rdp">Exposed RDP Guide</Link> — RDP (port 3389) is even more
                dangerous than SSH when exposed. If you found SSH, check for RDP too.</li>
            <li><Link to="/guides/exposed-admin-panels">Exposed Admin Panels Guide</Link> — Web-based
                admin interfaces (Jenkins, phpMyAdmin, etc.) are often found on the same servers.</li>
            <li><Link to="/guides/attack-surface-monitoring">Attack Surface Monitoring Guide</Link> —
                Learn how to continuously monitor for new SSH exposures before attackers find them.</li>
            <li><Link to="/exposed-ssh-monitoring">Exposed SSH Monitoring Solution</Link> —
                See how DriftAlarm continuously monitors for exposed SSH with automated drift alerts.</li>
          </ul>
        </section>

        <section className="guide-section">
          <h2>Summary: SSH Hardening Checklist</h2>
          <div className="quickstart-list">
            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Critical</span>
                <span className="quickstart-day-title">Must-Have Controls</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Disable password authentication</li>
                <li>Disable root login</li>
                <li>Use SSH key authentication only</li>
                <li>Keep OpenSSH updated</li>
              </ul>
            </div>

            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Important</span>
                <span className="quickstart-day-title">Strong Recommendations</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Implement fail2ban or equivalent</li>
                <li>Use AllowUsers/AllowGroups directives</li>
                <li>Disable empty passwords</li>
                <li>Set MaxAuthTries to 3</li>
              </ul>
            </div>

            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Best Practice</span>
                <span className="quickstart-day-title">Defense in Depth</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Use bastion host or VPN for access</li>
                <li>Implement MFA for administrative access</li>
                <li>Centralize and monitor SSH logs</li>
                <li>Consider certificate-based authentication</li>
              </ul>
            </div>
          </div>

          <div className="guide-cta-group" style={{ marginTop: '40px' }}>
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>→</span>
            </button>
          </div>
        </section>
        <GuideFAQ path="/guides/exposed-ssh" />
      </main>

      <PublicFooter />
    </div>
  );
}

export default ExposedSSHGuide;
